import { StreamActions, cache, visit } from "@hotwired/turbo"

// Un solo retorno, en memoria: agregar, cambiar cantidad o borrar el último ítem.
// El snapshot conserva productos/scroll; la respuesta aporta la barra confirmada.
let pendingReturn
let cartEntry

// Etapa E: la última barra conocida de la cuenta; un cuerpo con una barra más vieja la recibe antes de
// pintarse. Doc: _docs/frontend/catalog_presentation/storefront_quick_add.md → Decisiones (vuelta a la tienda)
let latestBar

const versionOf = container => Number(container?.dataset?.cartQuantitiesVersionValue) || 0

function samePage(left, right) {
  const a = new URL(left, window.location.href)
  const b = new URL(right, window.location.href)
  return a.origin === b.origin && a.pathname === b.pathname
}

// Recordar únicamente una entrada real tienda → carrito en esta WebView.
// Un carrito abierto directamente o desde checkout conserva el enlace normal.
document.addEventListener("turbo:visit", (event) => {
  const cartLink = document.querySelector(".floating-cart-link")
  const accountId = document.querySelector(".account-data")?.dataset.accountId
  const storeId = document.querySelector(".store-data")?.dataset.id
  if (cartLink && accountId && storeId && samePage(event.detail.url, cartLink.href)) {
    cartEntry = { url: event.detail.url, accountId, storeId }
  } else if (cartEntry && !samePage(event.detail.url, cartEntry.url)) {
    cartEntry = undefined
  }
})

document.addEventListener("turbo:click", (event) => {
  const link = event.target.closest?.("a[data-native-cart-return]")
  if (!link || !cartEntry || !samePage(window.location.href, cartEntry.url) ||
      link.dataset.nativeCartReturn !== cartEntry.storeId ||
      link.dataset.accountId !== cartEntry.accountId) return

  // El enlace normal inicia advance antes del pop en iOS: una respuesta rápida
  // puede reemplazar el snapshot. Pedir solo el pop evita esa segunda visita.
  event.preventDefault()
  // Cancelar turbo:click no cancela el href que seguiría la propia WebView.
  event.detail.originalEvent.preventDefault()
  if (pendingReturn) pendingReturn.returnURL = "/recede_historical_location"
  visit("/recede_historical_location")
})

function finishReturn() {
  if (!pendingReturn) return
  pendingReturn = undefined
  // Invalidar DESPUÉS del retorno evita restaurar otros carritos obsoletos,
  // sin borrar el snapshot que necesitamos para este regreso.
  cache.clear()
}

// Páginas de la tienda que llevan la barra: la tienda (ruta canónica y la corta del QR)
// Ver storefront_catalog.md y storefront_search.md → Fase 4
function isStoreURL(url) {
  const location = new URL(url, window.location.href)
  return pendingReturn.storeURLs.some(storeURL =>
    location.origin === storeURL.origin &&
      (location.pathname === storeURL.pathname ||
        location.pathname === `${storeURL.pathname}/search` ||
        location.pathname.startsWith(`${storeURL.pathname}/catalog/`))
  )
}

function rememberReturn({ accountId, storeId, storeURLs, bar, returnURL }) {
  // Cantidades sucesivas y el DELETE final reemplazan la barra pendiente sin
  // invalidar el snapshot que todavía necesitamos para regresar a la tienda.
  if (pendingReturn && (pendingReturn.accountId !== accountId || pendingReturn.storeId !== storeId)) {
    finishReturn()
  }
  pendingReturn = {
    accountId, storeId, bar, returnURL,
    storeURLs: storeURLs.filter(Boolean).map(url => new URL(url, window.location.href)),
    freshResponse: false
  }
}

// Registra la última barra de la cuenta (un clon), salvo que sea más vieja que la que ya se tiene
export function rememberBar(container, accountId) {
  if (!container || !accountId) return false
  const version = versionOf(container)
  if (latestBar && latestBar.accountId === String(accountId) && version < latestBar.version) return false
  latestBar = { bar: container.cloneNode(true), accountId: String(accountId), version }
  return true
}

// Un cuerpo con una barra más vieja que la última conocida la recibe; pendingReturn tiene prioridad
function restoreLatestBar(body) {
  if (!latestBar) return
  const container = body.querySelector("#floating-cart-bar-container")
  if (!container) return
  if (body.querySelector(".account-data")?.dataset.accountId !== latestBar.accountId) return
  if (versionOf(container) >= latestBar.version) return
  container.replaceWith(latestBar.bar.cloneNode(true))
}

document.addEventListener("turbo:before-fetch-request", (event) => {
  if (!event.target.matches?.("form[data-native-cart-resume]")) return
  const capability = event.target.dataset.nativeCartResume
  // "update" anuncia el borrado sin navegación si quedan ítems. Los módulos
  // anteriores no lo reconocen y conservan el redirect de respaldo.
  if (["true", "recede", "update"].includes(capability)) {
    event.detail.fetchOptions.headers["X-Native-Cart-Resume"] = capability
  }
})

StreamActions.native_cart_resume = function () {
  const returnAction = this.getAttribute("return-action")
  rememberReturn({
    accountId: this.getAttribute("account-id"),
    storeId: this.getAttribute("store-id"),
    // Rails aporta tanto la ruta canónica como la ruta corta usada por el QR.
    storeURLs: [this.getAttribute("store-url"), this.getAttribute("friendly-store-url")],
    bar: this.templateContent.querySelector("#floating-cart-bar-container"),
    returnURL: returnAction === "defer" ? undefined : returnAction === "recede"
      ? "/recede_historical_location" : "/resume_historical_location"
  })
  if (pendingReturn.returnURL) visit(pendingReturn.returnURL)
}

// Si el snapshot no existe, el GET normal es el respaldo. Su estado puede ser
// más reciente que la mutación (otra sesión); nunca sobreescribirlo con la barra.
document.addEventListener("turbo:before-fetch-response", (event) => {
  const response = event.detail.fetchResponse
  if (pendingReturn && response.succeeded && isStoreURL(response.location)) {
    pendingReturn.freshResponse = true
  }
})

document.addEventListener("turbo:before-render", (event) => {
  if (!pendingReturn) {
    restoreLatestBar(event.detail.newBody)
    return
  }
  const body = event.detail.newBody
  const sameAccount = body.querySelector(".account-data")?.dataset.accountId === pendingReturn.accountId
  const sameStore = body.querySelector(".store-data")?.dataset.id === pendingReturn.storeId

  if (!sameAccount || !sameStore || !isStoreURL(window.location.href)) {
    finishReturn()
    restoreLatestBar(body)
    return
  }

  if (!pendingReturn.freshResponse) {
    body.querySelector("#floating-cart-bar-container")?.replaceWith(pendingReturn.bar.cloneNode(true))
  }
})

// Consumir tras render/scroll. Si el usuario sale por otro camino, descartar
// el estado antes de navegar: no debe reaparecer en otra tienda o cuenta.
document.addEventListener("turbo:load", finishReturn)
document.addEventListener("turbo:before-visit", (event) => {
  if (!pendingReturn) return
  const url = new URL(event.detail.url, window.location.href)
  if (url.pathname !== pendingReturn.returnURL && !isStoreURL(url)) finishReturn()
})
